import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Global store for the install prompt event
let globalDeferredPrompt = null;

export function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(!!globalDeferredPrompt);
  const [isInstalled, setIsInstalled] = useState(
    window.matchMedia('(display-mode: standalone)').matches
  );

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      globalDeferredPrompt = e;
      setCanInstall(true);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      globalDeferredPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const install = async () => {
    if (!globalDeferredPrompt) return false;
    globalDeferredPrompt.prompt();
    const { outcome } = await globalDeferredPrompt.userChoice;
    globalDeferredPrompt = null;
    setCanInstall(false);
    return outcome === 'accepted';
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  return { canInstall, isInstalled: isStandalone, install, isIOS, isMobile };
}

export default function PWAInstallPrompt() {
  const { t } = useTranslation();
  const { canInstall, isInstalled, install, isIOS } = usePWAInstall();
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (isInstalled) return;
    const dismissed = sessionStorage.getItem('pwa-popup-dismissed');
    if (dismissed) return;

    const timer = setTimeout(() => setShowPopup(true), 5000);
    return () => clearTimeout(timer);
  }, [isInstalled]);

  const handleInstall = async () => {
    await install();
    setShowPopup(false);
  };

  const handleDismiss = () => {
    setShowPopup(false);
    sessionStorage.setItem('pwa-popup-dismissed', 'true');
  };

  if (!showPopup || isInstalled) return null;
  if (!canInstall && !isIOS) return null;

  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-5 duration-300 safe-bottom">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg text-void mb-1">
              {t('pwa.installTitle', 'Asenna Uneksia')}
            </h3>
            <p className="text-sm text-void/70 font-body mb-3">
              {isIOS 
                ? t('pwa.iosInstructions', 'Napauta jakamispainiketta ja valitse "Lisää Koti-valikkoon"')
                : t('pwa.installDescription', 'Lisää sovellus aloitusnäytölle nopeaa käyttöä varten')
              }
            </p>
            <div className="flex gap-2">
              {canInstall && (
                <Button
                  onClick={handleInstall}
                  className="bg-void text-white hover:bg-void/90 rounded-full text-sm px-4 py-2"
                  data-testid="pwa-install-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('pwa.install', 'Asenna')}
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={handleDismiss}
                className="text-void/60 hover:text-void rounded-full text-sm px-4 py-2"
                data-testid="pwa-dismiss-button"
              >
                {t('pwa.later', 'Myöhemmin')}
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-void/40 hover:text-void/60 transition-colors"
            aria-label="Sulje"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
